import React from 'react';

/**
 * Publishes `--og-assist-drawer-width` (a CSS custom property on the given container): the
 * horizontal width the OG Assist drawer occupies at the viewport's right edge, measured
 * live from the DOM. DefaultPageLayout.scss consumes it to slide the reporting-owned
 * Workspace surfaces (the rail + the expanded panel) left so they sit flush beside the
 * drawer instead of hidden behind it. The behaviour is identical on both hosts.
 *
 * The OG Assist drawer is `position: fixed`, anchored to the viewport's right edge, in both
 * hosts — but it lives in a different slot, so it is measured from `document`:
 * - Controlpanel (standalone Reporting): reporting's own `og-assist-drawer-slot`.
 * - PSP (Reporting inside the PSP shell): the shell's `psp-drawer-slot-region`, which also
 *   hard-clips the drawer to a fixed right-edge strip so it cannot itself be moved.
 *
 * The occupied width toggles (drawer alone vs the hamburger sub-panel widening it), so it is
 * measured rather than hardcoded; the drawer packages expose no width variable of their own.
 */
export const OG_ASSIST_DRAWER_WIDTH_VAR = '--og-assist-drawer-width';

const OG_ASSIST_SLOT_SELECTOR = '[data-testid="og-assist-drawer-slot"]';
const PSP_DRAWER_SLOT_SELECTOR = '[data-testid="psp-drawer-slot-region"]';
const OG_ASSIST_PAPER_SELECTOR = `${OG_ASSIST_SLOT_SELECTOR} .MuiDrawer-paper`;

// Where the OG Assist drawer sits in the DOM. On PSP it is the shell's slot region — a
// single clip box that wraps the whole drawer, including the hamburger sub-panel. On
// controlpanel it is the drawer paper(s) inside reporting's own slot (the hamburger adds a
// second paper), so all are considered and the leftmost on-screen edge wins.
function getOgAssistDrawerSurfaces(): HTMLElement[] {
  const pspRegion = document.querySelector<HTMLElement>(PSP_DRAWER_SLOT_SELECTOR);
  if (pspRegion !== null) {
    return [pspRegion];
  }
  return Array.from(document.querySelectorAll<HTMLElement>(OG_ASSIST_PAPER_SELECTOR));
}

// Occupied width = viewport width minus the leftmost on-screen edge of the drawer. Returns 0
// when the drawer is not on screen (closed, or absent), leaving the Workspace flush to the
// viewport edge.
function measureOgAssistDrawerWidth(): number {
  const viewportWidth = window.innerWidth;
  let leftmostEdge = viewportWidth;
  getOgAssistDrawerSurfaces().forEach((surface) => {
    const rect = surface.getBoundingClientRect();
    if (rect.width > 0 && rect.left < leftmostEdge) {
      leftmostEdge = rect.left;
    }
  });
  return viewportWidth - leftmostEdge;
}

// Wires up every trigger that can change the drawer width and returns a teardown function.
function observeOgAssistDrawer(container: HTMLElement, apply: () => void): () => void {
  apply();

  let transitionFrameId = 0;
  const stopFollowing = () => {
    if (transitionFrameId !== 0) {
      cancelAnimationFrame(transitionFrameId);
      transitionFrameId = 0;
    }
  };
  // On controlpanel the drawer slides open/closed by animating `transform` inside the
  // container, which never fires ResizeObserver. While that slide runs, re-measure every
  // frame so the Workspace tracks the moving edge.
  const followSlide = (event: TransitionEvent) => {
    if (event.propertyName !== 'transform') {
      return;
    }
    stopFollowing();
    const step = () => {
      apply();
      transitionFrameId = requestAnimationFrame(step);
    };
    step();
  };
  const settle = () => {
    stopFollowing();
    apply();
  };

  // Re-measure on viewport resize and when either slot resizes. Controlpanel: reporting's
  // slot resizes when the hamburger sub-panel widens the paper. PSP: the shell's slot region
  // is persistent but its width toggles 0 <-> full as the drawer slides open/closed (and
  // grows with the hamburger) — and that slide happens outside the container, so its
  // transitions never reach the listeners below; observing it re-measures the width once the
  // drawer settles instead of reading 0 the instant it starts opening.
  const resizeObserver = new ResizeObserver(apply);
  const slot = container.querySelector<HTMLElement>(OG_ASSIST_SLOT_SELECTOR);
  if (slot !== null) {
    resizeObserver.observe(slot);
  }
  const pspDrawerSlot = document.querySelector<HTMLElement>(PSP_DRAWER_SLOT_SELECTOR);
  if (pspDrawerSlot !== null) {
    resizeObserver.observe(pspDrawerSlot);
  }

  window.addEventListener('resize', apply);
  container.addEventListener('transitionrun', followSlide);
  container.addEventListener('transitionend', settle);
  container.addEventListener('transitioncancel', settle);

  return () => {
    stopFollowing();
    resizeObserver.disconnect();
    window.removeEventListener('resize', apply);
    container.removeEventListener('transitionrun', followSlide);
    container.removeEventListener('transitionend', settle);
    container.removeEventListener('transitioncancel', settle);
  };
}

export function useWorkspaceSurfaceOffset(
  containerRef: React.RefObject<HTMLElement>,
  isDrawerOpen: boolean
): void {
  React.useEffect(() => {
    const container = containerRef.current;
    if (container === null || !isDrawerOpen) {
      return undefined;
    }
    const apply = () => {
      container.style.setProperty(OG_ASSIST_DRAWER_WIDTH_VAR, `${measureOgAssistDrawerWidth()}px`);
    };
    const teardown = observeOgAssistDrawer(container, apply);
    return () => {
      teardown();
      // The drawer is closing: drop the width so the shifted Workspace surfaces snap back to
      // the viewport edge. The close animation may run outside the container (PSP shell), so
      // without this the last non-zero value would linger and keep them shifted.
      container.style.removeProperty(OG_ASSIST_DRAWER_WIDTH_VAR);
    };
  }, [containerRef, isDrawerOpen]);
}
